import React, { useState } from 'react';

// Define interfaces for API response types
interface PreRegisterResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validation
    if (!email || !name) {
      setError('Please enter both name and email');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    try {
      // Call the Cloudflare Worker to register the user
      const response = await fetch('https://preregister.cue.nexa-live.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          email: email
        })
      });
      
      const result: PreRegisterResponse = await response.json();
      
      if (result.success) {
        setSuccess('Thank you for pre-registering! We will notify you with updates.');
        setEmail('');
        setName('');
      } else {
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Registration failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-[var(--color-text)] flex flex-col items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-[#E8C9C4] via-[#F0D4D0] to-[#F8DFDA]">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-[var(--color-accent)] opacity-20 blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-60 h-60 rounded-full bg-[var(--color-secondary)] opacity-20 blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-[var(--color-accent)] opacity-10 blur-3xl"></div>

      <div className="w-full max-w-md rounded-xl p-8 relative z-10 overflow-hidden bg-[#FAE7E2] backdrop-filter backdrop-blur-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
        {/* Natural gradient transition around the card */}
        <div className="absolute -inset-2 bg-gradient-to-br from-white/10 via-white/5 to-transparent rounded-[28px] -z-5 pointer-events-none blur-md"></div>
        {/* Background image with styling effects */}
        <div className="absolute top-0 left-0 right-0 bottom-0 -z-10 overflow-hidden">
          <div className="w-[150%] h-[150%] absolute top-1/3 left-1/3 transform -translate-x-[55%] -translate-y-[65%]">
            <img 
              src="/no_back.png" 
              alt="" 
              className="w-full h-full object-contain opacity-30 blur-[2px] pointer-events-none brightness-105 contrast-80 saturate-70" 
            />
          </div>
          
          {/* Commented out blurred background effects */}
          {/* 
            <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/50 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-white/40 blur-3xl pointer-events-none"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-white/45 blur-2xl pointer-events-none"></div>
            <div className="absolute top-1/4 right-1/4 w-20 h-20 rounded-full bg-white/35 blur-xl pointer-events-none"></div>
            <div className="absolute bottom-1/4 left-1/3 w-24 h-24 rounded-full bg-white/30 blur-xl pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/15"></div>
          */}
        </div>
        {/* Logo */}
        <div className="flex justify-center items-center">
          <img src="/Cue_purple_star.png" alt="Cue Logo" className="h-48" />
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2 text-[var(--color-heading)]">Coming September 2025</h1>
          <p className="text-[var(--color-text-secondary)]">Thank you for your reservation</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-[var(--color-text)] text-sm">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
              className="w-full py-3 px-4 bg-[rgba(255,255,255,0.2)] border border-[rgba(255,255,255,0.3)] rounded-lg text-[var(--color-text)] placeholder:text-[var(--color-muted)]"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-[var(--color-text)] text-sm">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              disabled={loading}
              className="w-full py-3 px-4 bg-[rgba(255,255,255,0.2)] border border-[rgba(255,255,255,0.3)] rounded-lg text-[var(--color-text)] placeholder:text-[var(--color-muted)]"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="gradient-button w-full py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <span className="whitespace-nowrap px-2">Pre-register & I agree to receive email updates</span>
            )}
          </button>

          {error && (
            <div className="bg-red-500 bg-opacity-20 border border-red-300 text-[var(--color-text)] px-4 py-2 rounded-lg mb-6">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-500 bg-opacity-20 border border-green-300 text-[var(--color-text)] px-4 py-2 rounded-lg mb-6 mt-4">
              {success}
            </div>
          )}
        </form>
        
        <div className="mt-8 text-center">
          <p className="text-xs text-[var(--color-muted)]">
            © {new Date().getFullYear()} NexaLive Web Studio. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
